/**
 * Who can do what, as one table — the design's "Rôles & droits".
 *
 * The editor above it answers "what does this role grant"; this answers the
 * opposite question, which is the one asked in a meeting: *who* can refuse a
 * warranty, *who* can record a payment. Answering it from the editor means
 * opening six dialogs and remembering.
 *
 * **A wildcard is shown as what it is.** A role granted `billing.*` holds
 * every billing permission, and rendering that as an empty cell for each of
 * them would be a table that lies. The cell says "par module" so that the
 * reason the role has it is visible — and so nobody removes the wildcard
 * expecting the individual grants to survive.
 *
 * Grouped by module, because 65 permissions in one alphabetical list is a
 * table nobody reads twice.
 */

import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card, StatusDot } from "@hardauto/core-ui";

import type { Permission, Role } from "../api";
import { NAMESPACE } from "../i18n";
import "./admin.css";

export interface PermissionMatrixProps {
  roles: readonly Role[];
  permissions: readonly Permission[];
}

/** How a role holds a permission, if it does. */
type Holding = "direct" | "wildcard" | "all" | "none";

/**
 * Whether `role` holds `key`, and how.
 *
 * The matching rule is the server's, walked right to left: `billing.*`
 * satisfies `billing.document.read`, and `*` satisfies everything. Reported
 * with its *reason* rather than as a boolean, because "granted by a wildcard"
 * is what somebody needs to know before they try to remove it.
 */
function holding(role: Role, key: string): Holding {
  if (role.permissions.includes(key)) return "direct";
  if (role.permissions.includes("*")) return "all";

  let dot = key.lastIndexOf(".");
  while (dot !== -1) {
    if (role.permissions.includes(`${key.slice(0, dot)}.*`)) return "wildcard";
    dot = key.lastIndexOf(".", dot - 1);
  }
  return "none";
}

export function PermissionMatrix({ roles, permissions }: PermissionMatrixProps) {
  const { t } = useTranslation(NAMESPACE);

  /** Permissions by module, each module's own list in declaration order. */
  const grouped = useMemo(() => {
    const byModule = new Map<string, Permission[]>();
    for (const permission of permissions) {
      const list = byModule.get(permission.module) ?? [];
      list.push(permission);
      byModule.set(permission.module, list);
    }
    return [...byModule.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [permissions]);

  if (roles.length === 0 || permissions.length === 0) return null;

  return (
    <Card title={t("matrix.title")} description={t("matrix.description")} flush>
      {/* Its own scroll container: with six roles and sixty-five
          permissions the table is wider than the page, and the page itself
          must never scroll sideways. */}
      <div className="hardauto-admin__matrix">
        <table className="hardauto-table">
          <caption className="hardauto-visually-hidden">{t("matrix.caption")}</caption>
          <thead>
            <tr>
              <th scope="col">{t("matrix.permission")}</th>
              {roles.map((role) => (
                <th key={role.id} scope="col" className="hardauto-admin__matrix-role">
                  {role.name}
                </th>
              ))}
            </tr>
          </thead>
          {grouped.map(([module, entries]) => (
            <tbody key={module}>
              <tr className="hardauto-admin__matrix-group">
                {/* One heading row per module, spanning the table: a screen
                    reader hears "referential" once instead of a prefix on
                    every one of its rows. */}
                <th scope="colgroup" colSpan={roles.length + 1}>
                  {module}
                </th>
              </tr>
              {entries.map((permission) => (
                <tr key={permission.key}>
                  <th scope="row">
                    <span className="hardauto-admin__matrix-label">{permission.label}</span>
                    <code className="hardauto-cell__muted">{permission.key}</code>
                  </th>
                  {roles.map((role) => {
                    const held = holding(role, permission.key);
                    return (
                      <td key={role.id} className="hardauto-admin__matrix-cell">
                        {held === "none" ? (
                          // Empty rather than a cross: sixty-five crosses in
                          // a column is noise, and what the reader is looking
                          // for is the marks.
                          <span className="hardauto-visually-hidden">{t("matrix.no")}</span>
                        ) : (
                          <StatusDot
                            tone={held === "direct" ? "success" : "info"}
                            label={t(`matrix.${held}`)}
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      </div>
    </Card>
  );
}
